import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Api from '../../api/Api';
import i18n from '../../i18n';
import Availability from '../../models/Availability';
import Conversation from '../../models/Conversation';
import HomeModel from '../../models/Home';
import { buildFavoriteFolderName } from '@homeexchange/home';
import UserModel from '../../models/User';
import Analytics from '../../utils/analytics';
import Utils from '../../utils/utils';
import {
    BUTTON_BLOCKER_EVENT,
    HOME_VIEW_EVENT,
    MODAL_BLOCKER_EVENT,
    trackButtonClick,
    trackHomeview
} from '../../utils/utils-ts';
import Loading from '../common/Loading';
import Contact from '../popup/Contact/Contact';
import VerificationModal from '../popup/VerificationModal/VerificationModal';
import { HomeBlocker } from './contact-ts/home-blocker';
import { Button, Callout, Flex, IconButton, Modal } from '@homeexchange/design';
import Verification from '../popup/Contact/Verification';
import { getConversationsV2 } from '@homeexchange/conversations';
import useFavoriteModals from '../../hooks/useFavoriteModals';
import HomeFavoriteButton from './HomeFavoriteButton';
import HomeShareButton from './HomeShareButton';

export class ContactButton extends React.Component {
    static propTypes = {
        isAuthenticated: PropTypes.bool,
        accessToken: PropTypes.string.isRequired,
        homeId: PropTypes.number.isRequired,
        user: PropTypes.instanceOf(UserModel),
        home: PropTypes.instanceOf(HomeModel),
        userHomes: PropTypes.arrayOf(PropTypes.instanceOf(HomeModel)),
        availabilities: PropTypes.arrayOf(PropTypes.instanceOf(Availability)),
        homeFavorites: PropTypes.array,
        onFavoriteClick: PropTypes.func,
        onRemoveFavorite: PropTypes.func,
        onMoveFavorite: PropTypes.func
    };

    static defaultProps = {
        userHomes: [],
        availabilities: []
    };

    constructor(props) {
        super(props);

        this.state = {
            home: null,
            alreadyContacted: false,
            conversationInProgress: false,
            showModal: false
        };

        this.handleShowModal = this.handleShowModal.bind(this);
        this.handleHideModal = this.handleHideModal.bind(this);
        this.handleShowEllipsisModal = this.handleShowEllipsisModal.bind(this);
        this.handleHideEllipsisModal = this.handleHideEllipsisModal.bind(this);
        this.handleCollectionLinkClick = this.handleCollectionLinkClick.bind(this);
    }

    componentDidMount() {
        $.when(
            // find previous conversation — include archived/deleted statuses so
            // "already contacted" survives archiving or deletion from the inbox.
            getConversationsV2({
                filters: { 'e.home': this.props.homeId, status: Conversation.getContactedStatuses() },
                offset: 0,
                limit: 1
            }),
            // find previous conversation in progress
            getConversationsV2({
                filters: { 'e.home': this.props.homeId, 'e.status': [0, 1, 2] },
                offset: 0,
                limit: 1,
                orderBy: {
                    'c.createdAt': 'DESC'
                }
            })
        ).done((prevConv, prevConvInProgress) => {
            this.setState({
                alreadyContacted: prevConv.conversations.length > 0,
                conversationInProgress: _.first(prevConvInProgress.conversations)
            });
        });
    }

    isHomeCollectionOnly() {
        // user not collection and the home is collection
        const { home, user } = this.props;

        return (
            home?.get('is_he_collection') &&
            home?.get('is_search_he_collection_only') &&
            !user?.get('is_he_collection')
        );
    }

    isDisabled() {
        // user should accept contact on unavailable periods
        // or have at least one available period define in his calendar
        return (
            this.props.home &&
            !(
                this.props.home.get('contact_allowed') === HomeModel.CONTACT_ALLOWED_ON_UNAVAILABLE ||
                this.props.availabilities.filter(this.availablePeriodsFilter).length > 0
            )
        );
    }

    availablePeriodsFilter(availability) {
        return (
            availability.get('type') === Availability.AVAILABLE.type ||
            availability.get('type') === Availability.NON_RECIPROCAL.type ||
            availability.get('type') === Availability.RECIPROCAL.type
        );
    }
    isMobile() {
        return 'matchMedia' in window && window.matchMedia('(max-width: 767px)').matches;
    }

    renderFavorite() {
        return (
            <HomeFavoriteButton
                isFavorite={this.props.home.isFavorite(this.props.homeFavorites)}
                onClick={() => this.handleFavorite(this.props.isAuthenticated)}
            />
        );
    }

    renderShare() {
        return <HomeShareButton homeId={this.props.home.id} />;
    }

    handleCollectionLinkClick(e) {
        e.preventDefault();

        const eventData = {
            area: 'homeview',
            action: 'click',
            text: 'blocker_contact_collection',
            collection: true
        };
        Analytics.trackGTM('button_blocker', { event_data: eventData });

        window.open('/collection/subscription/payment', '_blank');
    }

    renderPrimary() {
        const { home, user } = this.props;
        const { conversationInProgress, alreadyContacted } = this.state;

        if (conversationInProgress) {
            return (
                <Button
                    href={i18n.t('exchange:conversation.url', { id: conversationInProgress.id })}
                    size="large"
                    isFullWidth
                >
                    {i18n.t('home:continue_conversation')}
                </Button>
            );
        }
        if (alreadyContacted) {
            return (
                <Button
                    size="large"
                    isFullWidth
                    onClick={this.handleShowModal}
                    isDisabled={this.isDisabled() || this.isHomeCollectionOnly()}
                >
                    {i18n.t('home:already_contacted')}
                </Button>
            );
        }
        if (
            home?.get('is_he_collection') &&
            home?.get('is_search_he_collection_only') &&
            !user?.isCollection() &&
            user?.isUserEligibleCollection()
        ) {
            return (
                <Button size="large" isFullWidth isDisabled>
                    {i18n.t('home:contact')}
                </Button>
            );
        }
        return (
            <Button
                size="large"
                isFullWidth
                onClick={this.handleShowModal}
                isDisabled={this.isDisabled() || this.isHomeCollectionOnly()}
            >
                {i18n.t('home:contact')}
            </Button>
        );
    }

    render() {
        const { user, home } = this.props;

        if (!home || !user) {
            return <Loading />;
        }

        if (home?.get('user').id == user?.id) {
            return false;
        }

        const { conversationInProgress } = this.state;

        return (
            <div className="contact-button">
                {this.renderAlert()}
                <div className="visible-xs">
                    <Flex direction="row" gap="2" flexWrap="nowrap" alignItems="center">
                        <IconButton
                            icon="DotsVertical"
                            hierarchy="secondary"
                            size="large"
                            onClick={this.handleShowEllipsisModal}
                            aria-label={i18n.t('home:more_options')}
                        />
                        {this.renderPrimary()}
                    </Flex>
                </div>
                <div className="hidden-xs">
                    <Flex gap="4" direction="columns" isFullWidth>
                        {this.renderPrimary()}
                        {conversationInProgress && (
                            <Button
                                hierarchy="secondary"
                                size="large"
                                isFullWidth
                                onClick={this.handleShowModal}
                                isDisabled={this.isDisabled() || this.isHomeCollectionOnly()}
                            >
                                {i18n.t('home:new_request')}
                            </Button>
                        )}
                        {this.renderFavorite()}
                        {this.renderShare()}
                    </Flex>
                </div>
                {this.renderModal()}
                {this.renderEllipsisModal()}
            </div>
        );
    }

    renderAlert() {
        const { home, user } = this.props;

        if (this.isDisabled()) {
            return <Callout status="warning">{i18n.t('home:calendar.only_available_periods')}</Callout>;
        }
        if (
            home?.get('is_he_collection') &&
            home?.get('is_search_he_collection_only') &&
            !user?.isCollection() &&
            user?.isUserEligibleCollection()
        ) {
            return (
                <Callout>
                    {i18n.t('home:contact.eligibleCollection')}
                    <p>
                        <a className="collection-link" href="#" onClick={this.handleCollectionLinkClick}>
                            {i18n.t('home:contact.eligibleCollectionCTA')}
                        </a>
                    </p>
                    <img src={`${Utils.getAssetsDomain()}/images/website/collection/logo.svg`} />
                </Callout>
            );
        }
        if (this.isHomeCollectionOnly()) {
            return <Callout>{i18n.t('collection:error.msg.cant_contact_collection_member')}</Callout>;
        }
        return false;
    }

    handleShowModal() {
        trackButtonClick(HOME_VIEW_EVENT, 'contact_home_view');
        const userHome = _.first(Api.Home.sortByStatusAndCompletionRate(this.props.userHomes));
        if (!userHome || userHome?.get('completion_rate') < HomeModel.MINIMUM_FILLING_TO_CONTACT) {
            trackHomeview(MODAL_BLOCKER_EVENT, 'contact_modal_blocker');
        }
        if (this.isMobile()) {
            this.setState({
                showEllipsisModal: false
            });
        }
        this.setState({ showModal: true });
    }
    handleShowEllipsisModal() {
        this.setState({
            showEllipsisModal: true
        });
    }

    handleFavorite(isAuthenticated) {
        if (isAuthenticated) {
            Analytics.trackGTM('MixpanelAddFavorites', { userStatus: this.props.user.getStatus() });

            const eventHandleFavorites = {
                action: 'click',
                text: 'cta_home_view',
                area: 'home view'
            };
            Analytics.trackGTM('HandleFavorites', { event_data: eventHandleFavorites });

            const isFavorite = this.props.home.isFavorite(this.props.homeFavorites);
            if (isFavorite && this.props.onRemoveFavorite) {
                this.props.onRemoveFavorite(this.props.homeId);
            } else if (!isFavorite && this.props.onFavoriteClick) {
                const prefillName = buildFavoriteFolderName({
                    city: this.props.home.firstAdmin(),
                    country: this.props.home.country()
                });
                this.props.onFavoriteClick(this.props.homeId, prefillName);
            }
        }
    }

    handleHideModal() {
        this.setState({ showModal: false });
    }
    handleHideEllipsisModal() {
        this.setState({ showEllipsisModal: false });
    }

    renderModal() {
        const { userHomes, user } = this.props;

        if (!userHomes || userHomes.length === 0) {
            return (
                <HomeBlocker
                    title={`${i18n.t('exchange:contact.cant_contact')}`}
                    message={`${i18n.t('exchange:contact.no_home_text')}`}
                    confirmAction={() => {
                        trackHomeview(BUTTON_BLOCKER_EVENT, 'contact_create_home', null);
                        window.location.href = i18n.t('home:home.create.url');
                    }}
                    confirmText={i18n.t('home:create_a_home')}
                    isOpen={this.state.showModal}
                    setIsOpen={this.handleHideModal}
                />
            );
        }

        const myHomes = Api.Home.sortByStatusAndCompletionRate(userHomes);
        const myHome = myHomes[0];

        if (myHome.get('completion_rate') < HomeModel.MINIMUM_FILLING_TO_CONTACT) {
            return (
                <HomeBlocker
                    title={`${i18n.t('exchange:contact.cant_contact')}`}
                    message={`${i18n.t('exchange:contact.home_filling_text')}`}
                    confirmAction={() => {
                        trackHomeview(BUTTON_BLOCKER_EVENT, 'contact_complete_home', null);
                        window.location.href = myHome.getEditUrl();
                    }}
                    confirmText={i18n.t('home:filling_my_home')}
                    isOpen={this.state.showModal}
                    setIsOpen={this.handleHideModal}
                />
            );
        }

        if (myHome.get('status') != HomeModel.STATUS_ONLINE) {
            return (
                <HomeBlocker
                    title={`${i18n.t('exchange:contact.cant_contact')}`}
                    message={`${i18n.t('exchange:contact.home_offline_text')}`}
                    confirmAction={() => {
                        trackHomeview(BUTTON_BLOCKER_EVENT, 'contact_home_offline', null);
                        window.location.href = myHome.getEditUrl();
                    }}
                    confirmText={i18n.t('home:publish_my_home')}
                    isOpen={this.state.showModal}
                    setIsOpen={this.handleHideModal}
                />
            );
        }

        if (!user.canCollectionMemberContact()) {
            return (
                <VerificationModal
                    show={this.state.showModal}
                    closeModal={this.handleHideModal}
                    homes={this.state.verificationHomes}
                />
            );
        }

        // Disallowcontact for non verified member
        if (!user.isVerified()) {
            return this.renderVerificationModal();
        }

        return this.renderContactFormModal();
    }

    renderVerificationModal() {
        const { user } = this.props;

        return <Verification showModal={this.state.showModal} onClose={this.handleHideModal} user={user} />;
    }

    renderContactFormModal() {
        return this.props.user && this.props.home ? (
            <Contact
                showModal={this.state.showModal}
                onCloseModal={this.handleHideModal}
                user={this.props.user}
                home={this.props.home}
                userHomes={this.props.userHomes}
                availabilities={this.props.availabilities}
            />
        ) : null;
    }

    renderEllipsisModal() {
        const { user } = this.props;
        const { conversationInProgress } = this.state;

        if (!user.canCollectionMemberContact()) {
            return (
                <VerificationModal
                    show={this.state.showModal}
                    closeModal={this.handleHideModal}
                    homes={this.state.verificationHomes}
                />
            );
        }

        return (
            <Modal
                isOpen={this.state.showEllipsisModal}
                onClose={this.handleHideEllipsisModal}
                isClosable
                areDividersVisible={false}
                size="small"
            >
                <div id="contact-ellipsis-modal-content">
                    <Flex gap="4" direction="column" isFullWidth>
                        {conversationInProgress && (
                            <Button
                                hierarchy="secondary"
                                size="large"
                                isFullWidth
                                onClick={this.handleShowModal}
                                isDisabled={this.isDisabled() || this.isHomeCollectionOnly()}
                            >
                                {i18n.t('home:new_request')}
                            </Button>
                        )}
                        {this.renderFavorite()}
                        {this.renderShare()}
                    </Flex>
                </div>
            </Modal>
        );
    }
}

function getHomeFromState(state, ownProps) {
    if (state.loadedHomes && ownProps.homeId && state.loadedHomes[ownProps.homeId]) {
        return state.loadedHomes[ownProps.homeId];
    }
    return null;
}

const mapStateToProps = (state, ownProps) => ({
    accessToken: state.auth.accessToken,
    userHomes: state.homes,
    availabilities: state.availabilities[ownProps.homeId],
    isAuthenticated: Boolean(state.auth.accessToken),
    user: state.user,
    home: getHomeFromState(state, ownProps),
    homeFavorites: state.homeFavorites
});

const ConnectedContactButton = connect(mapStateToProps)(ContactButton);

// Wrapper to add favorite modals functionality
export function ContactButtonWithModals(props) {
    const { handleFavoriteClick, handleRemoveFavorite, handleMoveFavorite, modals } = useFavoriteModals({
        trackingArea: 'home_view'
    });

    return (
        <>
            <ConnectedContactButton
                {...props}
                onFavoriteClick={handleFavoriteClick}
                onRemoveFavorite={handleRemoveFavorite}
                onMoveFavorite={handleMoveFavorite}
            />
            {modals}
        </>
    );
}

export default ContactButtonWithModals;
